import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import StyledTableCell from '../../../../components/StyledTableCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useTableContext } from '../../../../context/table-context';
import { useNavigate } from 'react-router';

function DashboardForecast({ collectionForecast }) {
  const navigate = useNavigate();

  const { clearTableFilters, setSelectedFundingIdsFilter } = useTableContext();

  const onItemCountClick = (fundingIds, message) => {
    clearTableFilters({
      tableName: 'fundings',
      initSortBy: 'id',
      initSortOrder: 'desc',
      initRowsPerPage: 25,
    });
    setSelectedFundingIdsFilter({ fundingIds: fundingIds.join(','), message });
    navigate('/fundings');
  };

  const getEarlyLateList = (prePayday, postPayday) => {
    if (prePayday && postPayday) {
      return [...prePayday.list, ...postPayday.list];
    }

    if (prePayday) {
      return [...prePayday.list];
    }

    if (postPayday) {
      return [...postPayday.list];
    }

    return [];
  };

  const renderEarlyLateCell = (value, messageOnClick) => (
    <div className="d-flex align-items-center justify-content-end">
      <div className="d-flex flex-column">
        {value.prePayday && (
          <div>{`Early: $${value.prePayday.sum} (${value.prePayday.count})`}</div>
        )}
        {value.postPayday && (
          <div>{`Late: $${value.postPayday.sum} (${value.postPayday.count})`}</div>
        )}
      </div>
      {(value.prePayday || value.postPayday) && (
        <Button
          className="bo-button-inline-link bo-inline-badge p-0"
          onClick={() =>
            onItemCountClick(getEarlyLateList(value.prePayday, value.postPayday), messageOnClick)
          }
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="mt-1 ms-2" />
        </Button>
      )}
    </div>
  );

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between">Repayment Forecast</Card.Header>
      {collectionForecast ? (
        <Card.Body>
          <TableContainer>
            <Table aria-labelledby="tableTitle" aria-label="enhanced table">
              <TableHead>
                <TableRow>
                  <StyledTableCell align="left">Date</StyledTableCell>
                  <StyledTableCell align="right">Payday</StyledTableCell>
                  <StyledTableCell align="right">Early/Late</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collectionForecast.MANUAL && (
                  <TableRow hover tabIndex={-1}>
                    <StyledTableCell align="left">
                      <strong>Manual with no date</strong>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {`$${Number(collectionForecast.MANUAL.sum).toLocaleString('en-US')} `}
                      {`(${collectionForecast.MANUAL.count})`}
                      {collectionForecast.MANUAL.list.length > 0 && (
                        <Button
                          className="bo-button-inline-link bo-inline-badge p-0 mb-1"
                          onClick={() =>
                            onItemCountClick(
                              collectionForecast.MANUAL.list,
                              'Dashboard → Repayment Forecast → Manual',
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="mb-0 ms-1" />
                        </Button>
                      )}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {renderEarlyLateCell(
                        collectionForecast.MANUAL,
                        'Dashboard → Repayment Forecast → Manual → Early/Late',
                      )}
                    </StyledTableCell>
                  </TableRow>
                )}
                {collectionForecast.FORCE && (
                  <TableRow hover tabIndex={-1}>
                    <StyledTableCell align="left">
                      <strong>Force</strong>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {`$${Number(collectionForecast.FORCE.sum).toLocaleString('en-US')} `}
                      {`(${collectionForecast.FORCE.count})`}
                      {collectionForecast.FORCE.list.length > 0 && (
                        <Button
                          className="bo-button-inline-link bo-inline-badge p-0 mb-1"
                          onClick={() =>
                            onItemCountClick(
                              collectionForecast.FORCE.list,
                              'Dashboard → Repayment Forecast → Force',
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="mt-1 ms-1" />
                        </Button>
                      )}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {renderEarlyLateCell(
                        collectionForecast.FORCE,
                        'Dashboard → Repayment Forecast → Force → Early/Late',
                      )}
                    </StyledTableCell>
                  </TableRow>
                )}
                {collectionForecast.IMMEDIATE && (
                  <TableRow hover tabIndex={-1}>
                    <StyledTableCell align="left">
                      <strong>Immediate</strong>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {`$${Number(collectionForecast.IMMEDIATE.sum).toLocaleString('en-US')} `}
                      {`(${collectionForecast.IMMEDIATE.count})`}
                      {collectionForecast.IMMEDIATE.list.length > 0 && (
                        <Button
                          className="bo-button-inline-link bo-inline-badge p-0 mb-1"
                          onClick={() =>
                            onItemCountClick(
                              collectionForecast.IMMEDIATE.list,
                              'Dashboard → Repayment Forecast → Immediate',
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="mt-1 ms-1" />
                        </Button>
                      )}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {renderEarlyLateCell(
                        collectionForecast.IMMEDIATE,
                        'Dashboard → Repayment Forecast → Immediate → Early/Late',
                      )}
                    </StyledTableCell>
                  </TableRow>
                )}
                {Object.entries(collectionForecast)
                  .filter(([key]) => key !== 'FORCE' && key !== 'IMMEDIATE' && key !== 'MANUAL')
                  .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
                  .map(([key, value]) => (
                    <TableRow key={key} hover tabIndex={-1}>
                      <StyledTableCell align="left">
                        <strong>{key}</strong>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {`$${Number(value.sum).toLocaleString('en-US')} `}
                        {`(${value.count})`}
                        {value.list.length > 0 && (
                          <Button
                            className="bo-button-inline-link bo-inline-badge p-0 mb-1"
                            onClick={() =>
                              onItemCountClick(
                                value.list,
                                `Dashboard → Repayment Forecast → ${key}`,
                              )
                            }
                          >
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              className="mt-1 ms-1"
                            />
                          </Button>
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {renderEarlyLateCell(
                          value,
                          `Dashboard → Repayment Forecast → ${key} → Early/Late`,
                        )}
                      </StyledTableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card.Body>
      ) : (
        <Card.Body className="bo-min-h-100" />
      )}
    </Card>
  );
}

export default DashboardForecast;
